import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getNotariesPaged,
  createNotary,
  updateNotary,
  deleteNotary,
} from 'services/notariesService';
import NotaryCreateEditModal from './NotaryCreateEditModal';
import ConfirmModal from '../../../../../components/ConfirmModal/ConfirmModal';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configNotariesI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';
import '../../../../../styles/naEeppTableGrid.scss';

const FILTER_VALUES = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
};

const CONVENIO_FILTER_VALUES = {
  all: 'all',
  yes: 'yes',
  no: 'no',
};

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactivo' || normalized === 'inactiva') return 'inactive';
  return 'active';
};

const toBackendStatusFilter = (value) => {
  switch (value) {
    case FILTER_VALUES.active:
      return 'Activos';
    case FILTER_VALUES.inactive:
      return 'Inactivos';
    default:
      return 'Todos';
  }
};

const toBackendConvenioFilter = (value) => {
  switch (value) {
    case CONVENIO_FILTER_VALUES.yes:
      return true;
    case CONVENIO_FILTER_VALUES.no:
      return false;
    default:
      return null;
  }
};

export default function ConfigNotaries({ DesignSystemComponents: dsProp } = {}) {
  const { language } = useLanguage();
  const notariesLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configNotariesI18n });
  const { showSnackbar } = useSnackbar();

  // Funciona como ruta top-level (DS por prop, menu EEPP) o dentro del Outlet de
  // Configuracion (DS por contexto). La prop tiene prioridad.
  const outletContext = useOutletContext() ?? {};
  const DesignSystemComponents = dsProp ?? outletContext.DesignSystemComponents;
  const { Button, Input, Table, Badge, IconButton, Modal, Dropdown, Card } =
    DesignSystemComponents ?? {};

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [statusFilterValue, setStatusFilterValue] = useState(FILTER_VALUES.active);
  const [convenioFilterValue, setConvenioFilterValue] = useState(CONVENIO_FILTER_VALUES.all);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formValue, setFormValue] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [searchDebounced, setSearchDebounced] = useState('');

  useEffect(() => {
    configNotariesI18n.changeLanguage(notariesLanguage);
  }, [notariesLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('config_notaries_filter_all'), value: FILTER_VALUES.all },
      { label: t('config_notaries_filter_active'), value: FILTER_VALUES.active },
      {
        label: t('config_notaries_filter_inactive'),
        value: FILTER_VALUES.inactive,
      },
    ],
    [t]
  );

  const convenioOptions = useMemo(
    () => [
      { label: t('config_notaries_filter_convenio_all'), value: CONVENIO_FILTER_VALUES.all },
      { label: t('config_notaries_filter_convenio_yes'), value: CONVENIO_FILTER_VALUES.yes },
      { label: t('config_notaries_filter_convenio_no'), value: CONVENIO_FILTER_VALUES.no },
    ],
    [t]
  );

  const selectedStatusFilter =
    statusOptions.find((option) => option.value === statusFilterValue) ||
    statusOptions[1];

  const selectedConvenioFilter =
    convenioOptions.find((option) => option.value === convenioFilterValue) ||
    convenioOptions[0];

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'name':
        return 'name';
      case 'comuna':
        return 'comuna';
      case 'email':
        return 'email';
      case 'convenio':
        return 'hasConvenio';
      case 'status':
        return 'isActive';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'comuna';
  const backendSortDesc =
    sortDirection === null ? false : sortDirection === 'desc';

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const mapNotaryRow = (notary) => ({
    id: notary.id,
    name: notary.name || '',
    comuna: notary.comuna || '',
    region: notary.region || '',
    address: notary.address || '',
    phone: notary.phone || '',
    contactPerson: notary.contactPerson || '',
    email: notary.email || '',
    hasConvenio: Boolean(notary.hasConvenio),
    notes: notary.notes || '',
    status: notary.isActive ? 'active' : 'inactive',
  });

  useEffect(() => {
    let mounted = true;

    const fetchNotaries = async () => {
      try {
        setLoading(true);

        const backendPage = page + 1;

        const response = await getNotariesPaged(
          backendPage,
          pageSize,
          toBackendStatusFilter(statusFilterValue),
          toBackendConvenioFilter(convenioFilterValue),
          searchDebounced || null,
          backendSortKey,
          backendSortDesc
        );

        if (!mounted) return;

        const mappedNotaries = response.notaries.map(mapNotaryRow);

        setRows(mappedNotaries);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading notaries:', error);
        if (mounted) {
          showSnackbar(error.customMessage || t('config_notaries_error_load'), 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchNotaries();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    searchDebounced,
    backendSortKey,
    backendSortDesc,
    statusFilterValue,
    convenioFilterValue,
    t,
  ]);

  const statusColor = (status) =>
    normalizeStatus(status) === 'active' ? 'success' : 'light';

  const openCreate = () => {
    setFormMode('create');
    setFormValue(null);
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setFormValue({ ...row, status: normalizeStatus(row?.status) });
    setIsFormOpen(true);
  };

  const openDelete = (row) => {
    setToDelete(row);
    setIsDeleteOpen(true);
  };

  const reloadNotaries = async () => {
    const backendPage = page + 1;
    const response = await getNotariesPaged(
      backendPage,
      pageSize,
      toBackendStatusFilter(statusFilterValue),
      toBackendConvenioFilter(convenioFilterValue),
      searchDebounced || null,
      backendSortKey,
      backendSortDesc
    );

    const mappedNotaries = response.notaries.map(mapNotaryRow);

    setRows(mappedNotaries);
    setTotal(response.total);
  };

  const saveForm = async (updatedValue) => {
    const dataToSave = updatedValue || formValue;

    if (!dataToSave?.name?.trim()) return;

    try {
      const payload = {
        name: dataToSave.name,
        comuna: dataToSave.comuna,
        region: dataToSave.region,
        address: dataToSave.address,
        phone: dataToSave.phone,
        contactPerson: dataToSave.contactPerson,
        email: dataToSave.email,
        hasConvenio: dataToSave.hasConvenio,
        notes: dataToSave.notes,
      };

      if (formMode === 'create') {
        await createNotary(payload);

        showSnackbar(t('config_notaries_success_created'), 'success');
      } else {
        await updateNotary(dataToSave.id, {
          ...payload,
          isActive: normalizeStatus(dataToSave.status) === 'active',
        });

        showSnackbar(t('config_notaries_success_updated'), 'success');
      }

      setIsFormOpen(false);
      await reloadNotaries();
    } catch (error) {
      console.error('Error saving notary:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_notaries_error_save');

      showSnackbar(errorMessage, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete?.id) return;

    try {
      await deleteNotary(toDelete.id);

      setIsDeleteOpen(false);
      setToDelete(null);

      showSnackbar(t('config_notaries_success_deleted'), 'success');

      await reloadNotaries();
    } catch (error) {
      console.error('Error deleting notary:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_notaries_error_delete');

      showSnackbar(errorMessage, 'error');
    }
  };

  if (!Button || !Input || !Table || !Badge || !IconButton || !Modal)
    return null;

  const Wrapper = Card || 'div';
  const wrapperProps = Card ? { specialPadding: 'p-4' } : { className: 'container-fluid p-4' };

  return (
    <Wrapper {...wrapperProps}>
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <div>
          <h2 className='mb-1'>{t('config_notaries_title')}</h2>
          <p className='caption font-light mb-0'>{t('config_notaries_subtitle')}</p>
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-4'>
          <Input
            icon='search'
            name='searchNotaries'
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('config_notaries_search_placeholder')}
            type='text'
            value={search}
          />
        </div>
        <div className='col-3'>
          <Dropdown
            options={statusOptions}
            value={selectedStatusFilter}
            onChange={(option) => {
              setStatusFilterValue(option?.value || FILTER_VALUES.all);
              setPage(0);
            }}
            placeholder={t('config_notaries_filter_all')}
            width='100%'
            isDisabled={loading}
          />
        </div>
        <div className='col-3'>
          <Dropdown
            options={convenioOptions}
            value={selectedConvenioFilter}
            onChange={(option) => {
              setConvenioFilterValue(option?.value || CONVENIO_FILTER_VALUES.all);
              setPage(0);
            }}
            placeholder={t('config_notaries_filter_convenio_all')}
            width='100%'
            isDisabled={loading}
          />
        </div>
        <div className='col-2'>
          <div className='d-flex justify-content-end'>
            <Button
              label={t('config_notaries_add_button')}
              size='sm'
              variant='primary'
              onClick={openCreate}
            />
          </div>
        </div>
      </div>

      <div className='table-container na-eepp-grid-6'>
        <Table
          columns={[
            { accessor: 'comuna', label: t('config_notaries_col_comuna'), sort: true },
            { accessor: 'name', label: t('config_notaries_col_name'), sort: true },
            { accessor: 'email', label: t('config_notaries_col_email'), sort: true },
            {
              accessor: 'convenio',
              label: t('config_notaries_col_convenio'),
              sort: true,
              render: (row) => (
                <Badge
                  text={
                    row.hasConvenio
                      ? t('config_notaries_convenio_yes')
                      : t('config_notaries_convenio_no')
                  }
                  color={row.hasConvenio ? 'primary' : 'light'}
                  size='sm'
                />
              ),
            },
            {
              accessor: 'status',
              label: t('config_notaries_col_status'),
              sort: true,
              render: (row) => (
                <Badge
                  text={
                    normalizeStatus(row.status) === 'active'
                      ? t('config_notaries_status_active')
                      : t('config_notaries_status_inactive')
                  }
                  color={statusColor(row.status)}
                  size='sm'
                />
              ),
            },
            {
              label: t('config_notaries_col_actions'),
              render: (row) => (
                <div className='d-flex gap-2'>
                  <IconButton
                    ariaLabel={t('config_notaries_action_edit')}
                    icon='pencil'
                    onClick={() => openEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('config_notaries_action_delete')}
                    icon='trash'
                    onClick={() => openDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                </div>
              ),
            },
          ]}
          data={rows}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(0);
          }}
          paginationMode='server'
          totalItems={total}
          paginator
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          loading={loading}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
        />
        {!loading && rows.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>{t('config_notaries_no_results')}</p>
          </div>
        )}
      </div>

      <NotaryCreateEditModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        show={isFormOpen}
        mode={formMode}
        value={formValue}
        onChange={setFormValue}
        onClose={() => setIsFormOpen(false)}
        onSave={saveForm}
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('config_notaries_confirm_delete_title')}
        message={
          toDelete
            ? t('config_notaries_confirm_delete_message', {
                name: toDelete.name,
              })
            : t('config_notaries_confirm_delete_fallback')
        }
        confirmLabel={t('config_notaries_confirm_delete_button')}
        cancelLabel={t('config_notaries_cancel')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </Wrapper>
  );
}
